import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import generarPDF from '../utils/generarPDF';
import useAnchoredModalStyle from '../utils/useAnchoredModalStyle';

function ModalExportarPDF({
  nombre,
  campos,
  sesiones,
  isakRecords = [],
  refISAKMap,
  isakAbiertoMap,
  setIsakAbiertoMap,
  isakModuloAbierto = true,
  setIsakModuloAbierto,
  firmaUrl,
  firmaTexto = null,
  recomRecords = [],        // array de { id, text, date, created_at }
  kinesioData = null,
  anchorRect = null,
  modoInicial = 'normal',
  onCerrar,
}) {
  const soloRecom = modoInicial === 'recomendaciones';

  const [incluyeFirma, setIncluyeFirma] = useState(!!firmaUrl);
  const [incluyeFirmaTexto, setIncluyeFirmaTexto] = useState(!!firmaTexto);

  // soloRecom: selección individual por registro
  // normal: toggle único para incluir todas las recomendaciones
  const [recomSeleccionados, setRecomSeleccionados] = useState(
    Object.fromEntries(recomRecords.map(r => [r.id, true]))
  );
  const [incluirRecomendaciones, setIncluirRecomendaciones] = useState(
    !soloRecom && recomRecords.length > 0
  );
  const toggleRecom = (id) => setRecomSeleccionados(prev => ({ ...prev, [id]: !prev[id] }));

  const [incluirKinesio, setIncluirKinesio] = useState(!soloRecom && !!kinesioData);
  const gmfcsRecords = kinesioData?.gmfcsRecords || [];
  const [gmfcsSeleccionados, setGmfcsSeleccionados] = useState(
    Object.fromEntries(gmfcsRecords.map(r => [r.id, !soloRecom]))
  );
  const toggleGMFCS = (id) => setGmfcsSeleccionados(prev => ({ ...prev, [id]: !prev[id] }));

  const threshRecords = kinesioData?.threshRecords || [];
  const [threshSeleccionados, setThreshSeleccionados] = useState(
    Object.fromEntries(threshRecords.map(r => [r.id, !soloRecom]))
  );
  const toggleThresh = (id) => setThreshSeleccionados(prev => ({ ...prev, [id]: !prev[id] }));

  // Selección múltiple de sesiones: objeto { id: bool }
  const [sesionesSeleccionadas, setSesionesSeleccionadas] = useState(
    soloRecom ? {} : Object.fromEntries(sesiones.map(s => [s.id, true]))
  );

  // Un checkbox por ficha ISAK
  const [isakSeleccionados, setIsakSeleccionados] = useState(
    Object.fromEntries(isakRecords.map(r => [r.id, !soloRecom]))
  );

  const [camposSeleccionados, setCamposSeleccionados] = useState(
    soloRecom ? {} : Object.fromEntries(campos.map(c => [c.label, true]))
  );

  const [generando, setGenerando] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfNombre, setPdfNombre] = useState('');

  const toggleCampo = (label) =>
    setCamposSeleccionados(prev => ({ ...prev, [label]: !prev[label] }));

  const toggleISAK = (id) =>
    setIsakSeleccionados(prev => ({ ...prev, [id]: !prev[id] }));

  const toggleSesion = (id) =>
    setSesionesSeleccionadas(prev => ({ ...prev, [id]: !prev[id] }));

  const algunaSesionSeleccionada = sesiones.some(s => sesionesSeleccionadas[s.id]);

  const seleccionarTodasSesiones = () =>
    setSesionesSeleccionadas(Object.fromEntries(sesiones.map(s => [s.id, true])));
  const deseleccionarTodasSesiones = () =>
    setSesionesSeleccionadas(Object.fromEntries(sesiones.map(s => [s.id, false])));

  const sesionesAExportar = () => sesiones.filter(s => !!sesionesSeleccionadas[s.id]);

  const formatFecha = (f) => { if (!f) return ''; const [y, m, d] = f.split('-'); return `${d}/${m}/${y}`; };
  const formatFechaISAK = (iso) => iso ? new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

  const handleGenerar = async (descargar = true) => {
    setGenerando(true);

    // Para cada ISAK seleccionado, abrir el acordeón si está cerrado para capturar el DOM
    const isakParaExportar = [];
    const haySeleccionISAK = isakRecords.some(r => isakSeleccionados[r.id]);
    if (haySeleccionISAK && !isakModuloAbierto && setIsakModuloAbierto) {
      setIsakModuloAbierto(true);
      await new Promise(r => setTimeout(r, 300));
    }
    for (const record of isakRecords) {
      if (!isakSeleccionados[record.id]) continue;
      const estaAbierto = !!isakAbiertoMap[record.id];
      if (!estaAbierto) {
        setIsakAbiertoMap(prev => ({ ...prev, [record.id]: true }));
        await new Promise(r => setTimeout(r, 600));
      }
      const nodo = refISAKMap?.current?.[record.id];
      isakParaExportar.push({
        titulo: record.title || 'Evaluación Antropométrica ISAK',
        fecha: formatFechaISAK(record.created_at),
        nodo,
      });
    }

    const camposFiltrados = campos.filter(c => camposSeleccionados[c.label] !== false);
    const sesionesParaExportar = sesionesAExportar();

    // Recomendaciones a exportar: selección individual (soloRecom) o todas (normal)
    const recomsParaExportar = soloRecom
      ? recomRecords.filter(r => recomSeleccionados[r.id])
      : (incluirRecomendaciones ? recomRecords : []);

    const secciones = {
      datosPersonales: !soloRecom && Object.values(camposSeleccionados).some(Boolean),
      historialSesiones: sesionesParaExportar.length > 0,
      recomendaciones: recomsParaExportar.length > 0,
      kinesio: !soloRecom && incluirKinesio && !!kinesioData,
    };

    try {
      const blob = await generarPDF({
        nombre,
        campos: camposFiltrados,
        sesiones: sesionesParaExportar,
        secciones,
        isakSecciones: isakParaExportar,
        firmaUrl: incluyeFirma ? firmaUrl : null,
        firmaTexto: incluyeFirmaTexto ? firmaTexto : null,
        recommendations: secciones.recomendaciones ? recomsParaExportar : null,
        kinesioData: secciones.kinesio ? {
          ...kinesioData,
          gmfcsIds: Object.entries(gmfcsSeleccionados).filter(([,v]) => v).map(([k]) => Number(k)),
          threshIds: Object.entries(threshSeleccionados).filter(([,v]) => v).map(([k]) => Number(k)),
        } : null,
        retornarBlob: true,
      });

      const archivo = `ficha-${nombre.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.pdf`;
      if (descargar) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = archivo; a.click();
        URL.revokeObjectURL(url);
        onCerrar();
      } else {
        setPdfBlob(blob);
        setPdfNombre(archivo);
      }
    } catch (err) {
      console.error('Error generando PDF:', err);
    } finally {
      setGenerando(false);
    }
  };

  const compartirWhatsApp = async () => {
    let blob = pdfBlob;
    if (!blob) { await handleGenerar(false); return; }
    const archivo = pdfNombre || 'ficha.pdf';
    const file = new File([blob], archivo, { type: 'application/pdf' });
    if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
      try { await navigator.share({ files: [file], title: `Ficha de ${nombre}` }); onCerrar(); return; }
      catch (err) { if (err.name !== 'AbortError') console.error(err); }
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = archivo; a.click();
    URL.revokeObjectURL(url);
    setTimeout(() => window.open('https://wa.me/', '_blank'), 500);
    onCerrar();
  };

  const recomsHayAlgo = soloRecom
    ? Object.values(recomSeleccionados).some(Boolean)
    : (incluirRecomendaciones && recomRecords.length > 0);

  const hayAlgo = Object.values(camposSeleccionados).some(Boolean)
    || algunaSesionSeleccionada
    || Object.values(isakSeleccionados).some(Boolean)
    || recomsHayAlgo
    || (incluirKinesio && !!kinesioData);

  // Safety net: mientras el modal está abierto, previene scroll horizontal a
  // nivel de documento por cualquier desborde residual.
  useEffect(() => {
    const prev = document.body.style.overflowX;
    document.body.style.overflowX = 'hidden';
    return () => { document.body.style.overflowX = prev; };
  }, []);

  // ── Posicionamiento del panel ─────────────────────────────────────────────
  // Centrado horizontal + borde superior a la altura del botón disparador,
  // con maxHeight clampeado al viewport (nunca se corta abajo) y recálculo en
  // resize. En ≤680px devuelve {} y el CSS mobile arma el bottom-sheet.
  const cardStyle = useAnchoredModalStyle(anchorRect, { maxWidth: 520, minVisible: 280 });

  // Portal a <body>: si el modal quedara dentro de la ficha, cualquier
  // transform/filter de un ancestro cambiaría el containing block del
  // position:fixed y los cálculos de anchorRect (viewport) dejarían de valer.
  return createPortal(
    <div className="modal-overlay modal-overlay-exportar" onClick={(e) => e.target === e.currentTarget && onCerrar()}>
      <div className="modal-card" style={cardStyle}>
        <div className="modal-header">
          <h3>Exportar PDF</h3>
          <button className="modal-btn-cerrar" onClick={onCerrar}>✕</button>
        </div>
        <div className="modal-body">

          {/* Campos personales */}
          {!soloRecom && (
            <div className="exportar-grupo">
              <div className="exportar-grupo-titulo">Datos del paciente</div>
              {campos.map(c => (
                <label key={c.label} className="exportar-seccion-item">
                  <input type="checkbox" className="exportar-check"
                    checked={camposSeleccionados[c.label] !== false}
                    onChange={() => toggleCampo(c.label)} />
                  <span>{c.label}</span>
                </label>
              ))}
            </div>
          )}

          {/* Sesiones — selección múltiple por checkboxes */}
          {!soloRecom && (
            <div className="exportar-grupo">
              <div className="exportar-grupo-titulo" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Historial de sesiones</span>
                {sesiones.length > 0 && (
                  <span className="exportar-sesion-acciones">
                    <button type="button" className="exportar-link-btn" onClick={seleccionarTodasSesiones}>Todas</button>
                    {' · '}
                    <button type="button" className="exportar-link-btn" onClick={deseleccionarTodasSesiones}>Ninguna</button>
                  </span>
                )}
              </div>
              {sesiones.length === 0 ? (
                <span className="exportar-nota">Sin sesiones registradas.</span>
              ) : (
                sesiones.map(s => (
                  <label key={s.id} className="exportar-seccion-item">
                    <input type="checkbox" className="exportar-check"
                      checked={!!sesionesSeleccionadas[s.id]}
                      onChange={() => toggleSesion(s.id)} />
                    <span>Sesión del {formatFecha(s.date)}</span>
                  </label>
                ))
              )}
            </div>
          )}

          {/* Fichas ISAK */}
          {!soloRecom && isakRecords.length > 0 && (
            <div className="exportar-grupo">
              <div className="exportar-grupo-titulo">Evaluaciones Antropométricas ISAK</div>
              {isakRecords.map(record => (
                <label key={record.id} className="exportar-seccion-item">
                  <input type="checkbox" className="exportar-check"
                    checked={!!isakSeleccionados[record.id]}
                    onChange={() => toggleISAK(record.id)} />
                  <div className="exportar-isak-info">
                    <span>{record.title || 'Evaluación Antropométrica ISAK'}</span>
                    <span className="exportar-isak-fecha">Creada el {formatFechaISAK(record.created_at)}</span>
                  </div>
                </label>
              ))}
            </div>
          )}

          {/* Recomendaciones */}
          {soloRecom && recomRecords.length > 0 && (
            // En modo "solo recomendaciones": un checkbox por registro para elegir
            // cuáles incluir en el PDF (todos preseleccionados).
            <div className="exportar-grupo">
              <div className="exportar-grupo-titulo" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Recomendaciones a incluir</span>
                <span className="exportar-sesion-acciones">
                  <button type="button" className="exportar-link-btn"
                    onClick={() => setRecomSeleccionados(Object.fromEntries(recomRecords.map(r => [r.id, true])))}>
                    Todas
                  </button>
                  {' · '}
                  <button type="button" className="exportar-link-btn"
                    onClick={() => setRecomSeleccionados(Object.fromEntries(recomRecords.map(r => [r.id, false])))}>
                    Ninguna
                  </button>
                </span>
              </div>
              {recomRecords.map(r => (
                <label key={r.id} className="exportar-seccion-item">
                  <input type="checkbox" className="exportar-check"
                    checked={!!recomSeleccionados[r.id]}
                    onChange={() => toggleRecom(r.id)} />
                  <div className="exportar-isak-info">
                    <span>{r.text?.slice(0, 60)}{r.text?.length > 60 ? '…' : ''}</span>
                    <span className="exportar-isak-fecha">{formatFecha(r.date)}</span>
                  </div>
                </label>
              ))}
            </div>
          )}
          {!soloRecom && recomRecords.length > 0 && (
            // En modo normal: toggle único para incluir todas las recomendaciones
            <div className="exportar-grupo">
              <label className="exportar-seccion-item">
                <input type="checkbox" className="exportar-check"
                  checked={incluirRecomendaciones}
                  onChange={() => setIncluirRecomendaciones(p => !p)} />
                <span>Recomendaciones / Sugerencias ({recomRecords.length})</span>
              </label>
            </div>
          )}

          {/* Kinesiología */}
          {!soloRecom && kinesioData && (
            <div className="exportar-grupo">
              <label className="exportar-seccion-item">
                <input type="checkbox" className="exportar-check"
                  checked={incluirKinesio}
                  onChange={() => setIncluirKinesio(p => !p)} />
                <span>Ficha kinesiológica (perfil, threshold)</span>
              </label>
              {incluirKinesio && gmfcsRecords.length > 0 && (
                <div style={{ marginLeft: 20, marginTop: 6 }}>
                  <div className="exportar-grupo-titulo" style={{ marginBottom: 6 }}>Evaluaciones GMFCS a incluir:</div>
                  {gmfcsRecords.map(r => {
                    const fecha = r.date ? new Date(r.date + 'T00:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Sin fecha';
                    const nivel = r.level ? `Nivel ${r.level}` : 'Sin nivel';
                    return (
                      <label key={r.id} className="exportar-seccion-item">
                        <input type="checkbox" className="exportar-check"
                          checked={!!gmfcsSeleccionados[r.id]}
                          onChange={() => toggleGMFCS(r.id)} />
                        <div className="exportar-isak-info">
                          <span>{nivel}</span>
                          <span className="exportar-isak-fecha">{fecha}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              {incluirKinesio && threshRecords.length > 0 && (
                <div style={{ marginLeft: 20, marginTop: 6 }}>
                  <div className="exportar-grupo-titulo" style={{ marginBottom: 6 }}>Registros Threshold a incluir:</div>
                  {threshRecords.map(r => {
                    const fecha = r.date ? new Date(r.date + 'T00:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Sin fecha';
                    return (
                      <label key={r.id} className="exportar-seccion-item">
                        <input type="checkbox" className="exportar-check"
                          checked={!!threshSeleccionados[r.id]}
                          onChange={() => toggleThresh(r.id)} />
                        <div className="exportar-isak-info">
                          <span>IMT {r.imt_max ?? '—'} · PEP {r.pep_max ?? '—'}</span>
                          <span className="exportar-isak-fecha">{fecha}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Firma dibujada */}
          {firmaUrl ? (
            <div className="exportar-grupo">
              <label className="exportar-seccion-item">
                <input type="checkbox" className="exportar-check"
                  checked={incluyeFirma}
                  onChange={() => setIncluyeFirma(p => !p)} />
                <span>Incluir firma dibujada</span>
              </label>
              {incluyeFirma && <img src={firmaUrl} alt="Firma" className="exportar-firma-preview" />}
            </div>
          ) : (
            <p className="exportar-nota">Sin firma dibujada. Podés subirla en Configuración → Mis datos.</p>
          )}

          {/* Firma de texto */}
          {firmaTexto && (
            <div className="exportar-grupo">
              <label className="exportar-seccion-item">
                <input type="checkbox" className="exportar-check"
                  checked={incluyeFirmaTexto}
                  onChange={() => setIncluyeFirmaTexto(p => !p)} />
                <span>Incluir firma de texto: <em>{firmaTexto}</em></span>
              </label>
            </div>
          )}

        </div>

        <div className="modal-acciones">
          <button className="modal-btn-cancelar" onClick={onCerrar} disabled={generando}>Cancelar</button>
          <button className="exportar-btn-wa" onClick={compartirWhatsApp} disabled={generando || !hayAlgo} title="Compartir por WhatsApp">
            {generando && !pdfBlob ? '...' : '📲 WhatsApp'}
          </button>
          <button className="modal-btn-guardar" onClick={() => handleGenerar(true)} disabled={generando || !hayAlgo}>
            {generando ? 'Generando...' : 'Descargar PDF'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ModalExportarPDF;
